import React, { useState } from 'react'
import { Autocomplete, LoadScript } from '@react-google-maps/api'
import { toast } from 'react-toastify'

import { Grid, TextField, Typography, Button, MenuItem } from '@mui/material'
import { useTheme } from '@material-ui/core'

import { useAddRemovePropertyForms } from '../../hooks/useAddRemovePropertyForms'
import { statesList } from '../../helpers'
import { StyledFormContainer, SectionTitle } from './styles'
import { FormUsersSection, hasIncompleteFormUsers } from './FormUsersSection'

interface Props {
    organization: number | null
}

export const AddPropertyFormContainer = ({ organization }: Props) => {
    const {
        addPropertyFormState,
        addPropertyFormDispatch,
        createAddPropertyForm,
    } = useAddRemovePropertyForms(organization)

    const theme = useTheme()
    const [showValidation, setShowValidation] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (hasIncompleteFormUsers(addPropertyFormState)) {
            setShowValidation(true)
            return
        }

        // Filter valid other users
        const validOtherUsers = addPropertyFormState.other_users?.filter(
            (user) => user.name && user.email,
        )

        // Create a cleaned version of the form data
        const formData = {
            ...addPropertyFormState,
            // Only include property_manager if both name and email are filled
            property_manager:
                addPropertyFormState.property_manager?.name &&
                addPropertyFormState.property_manager?.email
                    ? addPropertyFormState.property_manager
                    : undefined,
            // Only include other_users if there are valid users
            other_users: validOtherUsers?.length ? validOtherUsers : undefined,
            organization: organization ?? 0,
            notes: addPropertyFormState.notes || null,
            billing_email: addPropertyFormState.billing_email || null,
        }

        await createAddPropertyForm(formData).then(() => {
            toast.success('Property request sent successfully')
            addPropertyFormDispatch({
                type: 'RESET_FORM',
            })
        })
    }

    const handleFieldChange = (field: string) => (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        addPropertyFormDispatch({
            type: 'UPDATE_FIELD',
            field,
            value: e.target.value,
        })
    }

    const handlePlaceSelect = (
        autocomplete: google.maps.places.Autocomplete,
    ) => {
        const place = autocomplete.getPlace()
        if (place && place.address_components) {
            const addressComponents = place.address_components

            const streetNumber =
                addressComponents.find((component) =>
                    component.types.includes('street_number'),
                )?.long_name || ''

            const route =
                addressComponents.find((component) =>
                    component.types.includes('route'),
                )?.long_name || ''

            const city =
                addressComponents.find((component) =>
                    component.types.includes('locality'),
                )?.long_name || ''

            const state =
                addressComponents.find((component) =>
                    component.types.includes('administrative_area_level_1'),
                )?.short_name || ''

            const zipCode =
                addressComponents.find((component) =>
                    component.types.includes('postal_code'),
                )?.short_name || ''

            // Update form state with new values
            addPropertyFormDispatch({
                type: 'UPDATE_FIELD',
                field: 'address',
                value: `${streetNumber} ${route}`.trim(),
            })
            addPropertyFormDispatch({
                type: 'UPDATE_FIELD',
                field: 'city',
                value: city,
            })
            addPropertyFormDispatch({
                type: 'UPDATE_FIELD',
                field: 'state',
                value: state,
            })
            addPropertyFormDispatch({
                type: 'UPDATE_FIELD',
                field: 'zip_code',
                value: zipCode,
            })
        }
    }

    return (
        <StyledFormContainer maxWidth="lg">
            <Typography variant="h4" gutterBottom>
                Request to Add New Property
            </Typography>
            <div className="form-content">
                <form className="form-section" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Property Details */}
                        <Grid item xs={12}>
                            <SectionTitle variant="h6">
                                Property Details
                            </SectionTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Property Name"
                                value={addPropertyFormState.property_name}
                                onChange={handleFieldChange('property_name')}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Unit Count"
                                value={addPropertyFormState.unit_count}
                                onChange={handleFieldChange('unit_count')}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                value={addPropertyFormState.start_date}
                                onChange={handleFieldChange('start_date')}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="email"
                                label="Billing Email"
                                value={addPropertyFormState.billing_email ?? ''}
                                onChange={handleFieldChange('billing_email')}
                            />
                        </Grid>

                        {/* Address Section */}
                        <Grid item xs={12}>
                            <SectionTitle variant="h6">Address</SectionTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <LoadScript
                                googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                                libraries={['places']}
                            >
                                <Autocomplete
                                    onLoad={(autocomplete) => {
                                        autocomplete.addListener(
                                            'place_changed',
                                            () =>
                                                handlePlaceSelect(autocomplete),
                                        )
                                    }}
                                >
                                    <div className="address-autocomplete">
                                        <TextField
                                            fullWidth
                                            label="Street Address"
                                            value={addPropertyFormState.address}
                                            onChange={handleFieldChange(
                                                'address',
                                            )}
                                            required
                                        />
                                    </div>
                                </Autocomplete>
                            </LoadScript>
                        </Grid>
                        <Grid container item xs={12} spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    value={addPropertyFormState.city}
                                    onChange={handleFieldChange('city')}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="State"
                                    value={addPropertyFormState.state}
                                    onChange={handleFieldChange('state')}
                                    required
                                >
                                    {statesList.map((state) => (
                                        <MenuItem key={state} value={state}>
                                            {state}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="ZIP Code"
                                    value={addPropertyFormState.zip_code}
                                    onChange={handleFieldChange('zip_code')}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <FormUsersSection
                            value={addPropertyFormState}
                            showValidation={showValidation}
                            onManagerChange={(managerType, field, value) =>
                                addPropertyFormDispatch({
                                    type: 'UPDATE_MANAGER',
                                    managerType,
                                    field,
                                    value,
                                })
                            }
                            onOtherUserChange={(index, field, value) =>
                                addPropertyFormDispatch({
                                    type: 'UPDATE_OTHER_USER',
                                    index,
                                    field,
                                    value,
                                })
                            }
                            onAddOtherUser={() =>
                                addPropertyFormDispatch({
                                    type: 'ADD_OTHER_USER',
                                })
                            }
                            onRemoveOtherUser={(index) =>
                                addPropertyFormDispatch({
                                    type: 'REMOVE_OTHER_USER',
                                    index,
                                })
                            }
                        />

                        <Grid item xs={12}>
                            <SectionTitle variant="h6">Notes</SectionTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notes"
                                value={addPropertyFormState.notes ?? ''}
                                onChange={handleFieldChange('notes')}
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                sx={{ mt: 2 }}
                                style={{
                                    textTransform: 'none',
                                    backgroundColor: theme.palette.primary.dark,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>

                <div className="map-section">
                    <iframe
                        width="100%"
                        loading="lazy"
                        src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                        &q=${addPropertyFormState.city}_${addPropertyFormState.state}`}
                    />
                </div>
            </div>
        </StyledFormContainer>
    )
}
